import { useState, useEffect, useCallback } from 'react';

const DEFAULT_KEY = 'koob-sidebar-collapsed';
const EVENT_NAME = 'koob-sidebar-collapse-change';

export default function useKoobSidebarCollapsed(storageKey = DEFAULT_KEY) {
  const [collapsed, setCollapsedRaw] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  const setCollapsed = useCallback(
    (value) => {
      const next = typeof value === 'function' ? value(collapsed) : value;
      setCollapsedRaw(next);
      try {
        localStorage.setItem(storageKey, String(next));
      } catch {
        /* noop */
      }
      window.dispatchEvent(
        new CustomEvent(EVENT_NAME, { detail: { key: storageKey } }),
      );
    },
    [collapsed, storageKey],
  );

  useEffect(() => {
    const handleCustom = (e) => {
      if (e.detail?.key === storageKey) {
        setCollapsedRaw(localStorage.getItem(storageKey) === 'true');
      }
    };
    const handleStorage = (e) => {
      if (e.key === storageKey) {
        setCollapsedRaw(e.newValue === 'true');
      }
    };
    window.addEventListener(EVENT_NAME, handleCustom);
    window.addEventListener('storage', handleStorage);
    // A child's effect runs before this one: a page that folds the sidebar on
    // mount (hotel search) sends its event before we listen. Read it back.
    try {
      setCollapsedRaw(localStorage.getItem(storageKey) === 'true');
    } catch {
      /* noop */
    }
    return () => {
      window.removeEventListener(EVENT_NAME, handleCustom);
      window.removeEventListener('storage', handleStorage);
    };
  }, [storageKey]);

  return [collapsed, setCollapsed] as const;
}
